import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CopyButton from '../components/CopyButton.js';

const MCP_URL = 'https://forms.conversionhive.au/mcp';
const OAUTH_DISCOVERY = 'https://forms.conversionhive.au/.well-known/oauth-authorization-server';

// Body of the page (after the URL banner). Plain markdown rendered with
// react-markdown — same pattern as DocsUsage.tsx.
const MCP_DOCS_MD = `## What is this?

This platform exposes a **Model Context Protocol (MCP)** endpoint so AI
assistants — Claude Desktop, Claude.ai, Claude Code, ChatGPT custom
connectors — can:

- Create forms for client websites end-to-end
- Define form fields and copy
- Manage CORS allowlists and webhook integrations
- Read submissions, search them, and report stats
- Hand the AI ready-to-paste HTML embeds for the static sites it builds

Once connected, you can say things like *"Build me a contact form for Acme
Windows and put it on their site"* and the AI will provision it, get the
embed code, and inject it into the page — without you ever opening this
dashboard.

---

## Authentication

The MCP endpoint uses **Google OAuth 2.1 with PKCE**. There is no static
API key. The first time an AI assistant connects:

1. It auto-discovers the OAuth flow via the \`.well-known\` URLs.
2. A browser pops up and asks you to sign in with Google.
3. Your email is checked against the platform's \`ALLOWED_EMAILS\` allowlist
   (the same list that gates this dashboard).
4. A 7-day access token is issued automatically and stored by your MCP
   client. After 7 days, you'll be prompted to sign in again (usually
   one click since Google's session is still alive).

You never paste or manage tokens manually. If an email isn't in
\`ALLOWED_EMAILS\`, sign-in fails with "Access denied".

---

## Connecting Claude Desktop

Edit \`claude_desktop_config.json\`
(macOS path: \`~/Library/Application Support/Claude/claude_desktop_config.json\`):

\`\`\`json
{
  "mcpServers": {
    "form-handler": {
      "transport": {
        "type": "http",
        "url": "https://forms.conversionhive.au/mcp"
      }
    }
  }
}
\`\`\`

Restart Claude Desktop. A browser tab opens for Google sign-in — that's it.

---

## Connecting Claude.ai

Settings → **Connectors** → **Add custom connector**

- **URL:** \`https://forms.conversionhive.au/mcp\`
- Click **Connect**, complete Google sign-in.

---

## Connecting Claude Code

One command:

\`\`\`sh
claude mcp add --transport http form-handler https://forms.conversionhive.au/mcp
\`\`\`

Browser pops up for Google sign-in.

---

## Connecting ChatGPT (custom GPTs / Actions)

ChatGPT supports MCP via custom connectors with OAuth — point it at
\`https://forms.conversionhive.au/mcp\` and ChatGPT walks you through the
Google auth flow.

---

## What the AI can do once connected

### Read tools (no confirmation needed)

| Tool | Purpose |
|---|---|
| \`list_clients\` | Find a form by name when you don't know the ID |
| \`get_client\` | Full config + form schema for one form |
| \`list_submissions\` | Submissions for a form (filter by status / date) |
| \`get_submission\` | Single submission with parsed form_data |
| \`search_submissions\` | Substring search across form_data |
| \`get_stats\` | Aggregate counts (email/webhook sent/failed/pending) |
| \`get_embed_code\` | Paste-ready iframe / script_tag / raw_html snippets |

### Write tools (no confirmation needed — reversible)

| Tool | Purpose |
|---|---|
| \`create_form\` | Provision a new form end-to-end and return embed code |
| \`update_form_schema\` | Change fields, button text, success message |
| \`update_form_settings\` | Webhook, notify_emails, Turnstile, pause/resume |
| \`add_allowed_origin\` | Add a website origin to the CORS allowlist |

### Destructive tools (require explicit confirmation)

These trigger a confirmation dialog in Claude Desktop / Claude.ai and
require a \`confirm: true\` argument that the AI is instructed to never
set without your explicit yes.

| Tool | What it does |
|---|---|
| \`archive_form\` | Hides the form, stops accepting submissions, hard-deletes in 30 days |
| \`remove_allowed_origin\` | Removes a site from CORS allowlist (existing embeds break) |
| \`delete_submission\` | Soft-deletes a submission, hard-deletes in 7 days |

For reversible operations (pause, disable email, clear webhook) the AI
uses \`update_form_settings\` — no confirmation prompt.

---

## What the AI knows automatically

The server exposes three read-only **resources** the AI fetches on demand:

| Resource URI | What it covers |
|---|---|
| \`forms://workflow-guide\` | End-to-end playbook: when to call each tool, embed flavour decision tree, webhook setup, permission model |
| \`forms://webhook-payload\` | JSON shape, \`X-Form-Signature\` HMAC verification, retry behaviour |
| \`forms://field-types\` | All 8 field types + common recipes (contact form, quote request, newsletter) |

Plus two **prompts** the user can trigger by name from the MCP UI:

- \`build_form_for_website\` — guided playbook for new forms
- \`add_form_to_existing_site\` — embed an existing form on a new site

---

## Try it

Once connected, try saying:

> *"Build a contact form for Acme Windows. Notifications to david@conversionhive.com.au. Site is acmewindows.com.au."*

The AI should call \`create_form\` once and hand you back an iframe snippet
ready to paste into the site — no follow-up questions needed.

---

## Troubleshooting

**Sign-in says "Access denied"**
Your Google account isn't in \`ALLOWED_EMAILS\`. Add it via
\`npx wrangler secret put ALLOWED_EMAILS\` (worker secret —
comma-separated list).

**Claude Desktop doesn't open a browser**
Make sure the \`transport.type\` is \`"http"\` not \`"sse"\`. Restart Claude
Desktop after editing the config.

**Tools/prompts not showing up after deploy**
Disconnect and reconnect the MCP server in your client — tool/prompt lists
are cached per session.

**Want to verify the endpoint is alive?**
\`\`\`sh
curl -s ${OAUTH_DISCOVERY}
\`\`\`
Should return JSON with \`authorization_endpoint\`, \`token_endpoint\`,
\`registration_endpoint\`.
`;

export default function DocsMcp() {
  return (
    <div className="docs-page">
      <h1 style={{ marginBottom: '0.5rem' }}>MCP — AI integration</h1>
      <p style={{ color: '#475569', marginBottom: '1.5rem' }}>
        Connect Claude, ChatGPT, or any MCP-compatible AI assistant to this
        platform. Google OAuth handles authentication automatically.
      </p>

      {/* Highlighted endpoint banner — light card with a vivid URL bar. */}
      <div
        style={{
          background: '#ffffff',
          padding: '1.25rem',
          borderRadius: '10px',
          marginBottom: '2rem',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
        }}
      >
        <div
          style={{
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#64748b',
            marginBottom: '0.6rem',
          }}
        >
          MCP endpoint URL
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          {/* Plain div (not <code>) so the global .docs-page code rule
              doesn't repaint it pink-on-grey. */}
          <div
            style={{
              flex: '1 1 320px',
              minWidth: 0,
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              padding: '0.6rem 0.85rem',
              borderRadius: '6px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.95rem',
              fontWeight: 500,
              color: '#0f172a',
              wordBreak: 'break-all',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {MCP_URL}
          </div>
          <CopyButton text={MCP_URL} label="📋 Copy URL" />
        </div>
        <div
          style={{
            fontSize: '0.85rem',
            color: '#475569',
            marginTop: '0.85rem',
            lineHeight: 1.5,
          }}
        >
          Paste this URL into any MCP-compatible client. The first connection
          will open a browser tab for Google sign-in. No API key needed.
        </div>
      </div>

      <ReactMarkdown remarkPlugins={[remarkGfm]}>{MCP_DOCS_MD}</ReactMarkdown>
    </div>
  );
}
